/**
 * BarometerNav – einheitliche Navigation für alle Seiten.
 * Desktop: Logo | Mein Paket™ | Barometer ▾ (Dropdown) | Pakete ▾ | [Aktuell]
 * Mobile:  Logo | Hamburger-Menü (vollständig)
 */
import React, { useState, useRef, useEffect } from "react";
import { PAKET_PRICES, formatPrice } from "@shared/prices";

const LOGO_URL = "/manus-storage/logo_2d439bd1.jpg";

const BAROMETERS = [
  { href: "/sensorik",                label: "Sensorik",      icon: "/manus-storage/11_094992a3.svg", color: "#6d0000", desc: "Sensorisches Marketing" },
  { href: "/lokal",                   label: "Lokal",         icon: "/manus-storage/1_55991aa3.svg",  color: "#115751", desc: "Lokale Sichtbarkeit" },
  { href: "/geo-barometer",           label: "GEO / KI",      icon: "/manus-storage/7_8bcad2d2.svg",  color: "#1a3a5c", desc: "KI-Sichtbarkeit" },
  { href: "/seo-barometer",           label: "SEO",           icon: "/manus-storage/23_1ab7147e.svg", color: "#0a5c36", desc: "SEO-Grundlagen-Check" },
  { href: "/conversion-barometer",    label: "Conversion",    icon: "/manus-storage/33_d51b7819.svg", color: "#b45309", desc: "Conversion-Optimierung" },
  { href: "/accessibility-barometer", label: "Accessibility", icon: "/manus-storage/16_9c3f7718.svg", color: "#3730a3", desc: "Barrierefreiheit & WCAG" },
  { href: "/datenschutz-barometer",   label: "Datenschutz",   icon: "/manus-storage/29_7f3bb394.svg", color: "#0f766e", desc: "DSGVO & Compliance" },
  { href: "/wettbewerb-barometer",    label: "Wettbewerb",    icon: "/manus-storage/41_3269a7aa.svg", color: "#7c2d12", desc: "Wettbewerbs-Analyse" },
  { href: "/ecommerce-barometer",     label: "E-Commerce",    icon: "/manus-storage/21_453003f0.svg", color: "#4f46e5", desc: "Shop & Verkauf" },
];

const PACKAGES = [
  { href: "/website-realitaetscheck",   label: "Basic Website-RealitätsCheck™",   price: formatPrice(PAKET_PRICES.website),   desc: "SEO · Lokal · Access. · Datenschutz" },
  { href: "/marketing-realitaetscheck", label: "Pro Website-RealitätsCheck™", price: formatPrice(PAKET_PRICES.marketing), desc: "7 Barometer · Sichtbarkeit & Wirkung" },
  { href: "/komplett-paket",            label: "Komplett Website-RealitätsCheck™",           price: formatPrice(PAKET_PRICES.komplett),  desc: "Alle 9 Barometer · Bestes Preis-Leistungs-Verhältnis" },
];

/** Misst die echte Pixelbreite beider Zeilen und gleicht WERBEAGENTUR per letter-spacing an */
function LogoText() {
  const waRef = useRef<HTMLSpanElement>(null);
  const lgRef = useRef<HTMLSpanElement>(null);
  const [waLS, setWaLS] = useState("0.02em");

  useEffect(() => {
    function adjust() {
      const wa = waRef.current;
      const lg = lgRef.current;
      if (!wa || !lg) return;
      // Messe natürliche Breite von LARISSA GIRENKO (ls=0.02em)
      const lgW = lg.getBoundingClientRect().width;
      // Messe natürliche Breite von WERBEAGENTUR (ls=0em als Basis)
      wa.style.letterSpacing = "0em";
      const waW0 = wa.getBoundingClientRect().width;
      // Differenz aufteilen auf 12 Zeichen (WERBEAGENTUR)
      // Jeder Buchstabe bekommt extra (lgW - waW0) / 12 px
      const extraPx = (lgW - waW0) / 12;
      const fs = parseFloat(getComputedStyle(wa).fontSize); // px
      const extraEm = extraPx / fs;
      const newLS = Math.max(0, extraEm).toFixed(4) + "em";
      setWaLS(newLS);
    }
    // Warte auf Schriftladen
    if (document.fonts?.ready) {
      document.fonts.ready.then(adjust);
    } else {
      setTimeout(adjust, 300);
    }
    window.addEventListener("resize", adjust);
    return () => window.removeEventListener("resize", adjust);
  }, []);

  const baseStyle: React.CSSProperties = {
    fontFamily: "'Bebas Neue', sans-serif",
    fontSize: "12px",
    lineHeight: 1.1,
    fontWeight: 900,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    display: "block",
  };

  return (
    <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start" }}>
      <span ref={waRef} style={{ ...baseStyle, color: "#000000", letterSpacing: waLS }}>WERBEAGENTUR</span>
      <span ref={lgRef} style={{ ...baseStyle, color: "#6d0000", letterSpacing: "0.02em" }}>LARISSA GIRENKO</span>
      <span style={{ fontFamily: "'DM Sans', sans-serif", color: "#888888", fontSize: "8px", lineHeight: 1.2, letterSpacing: "0.06em", fontWeight: 400, whiteSpace: "nowrap", display: "block", marginTop: "2px" }}>Dein Schlüssel zum Marketingerfolg.</span>
    </div>
  );
}

interface BarometerNavProps {
  current?: string;
  accentColor?: string;
}

export function BarometerNav({ current = "", accentColor = "#6d0000" }: BarometerNavProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [barometerOpen, setBarometerOpen] = useState(false);
  const [paketeOpen, setPaketeOpen] = useState(false);
  const barometerRef = useRef<HTMLDivElement>(null);
  const paketeRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (barometerRef.current && !barometerRef.current.contains(e.target as Node)) {
        setBarometerOpen(false);
      }
      if (paketeRef.current && !paketeRef.current.contains(e.target as Node)) {
        setPaketeOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Close mobile on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [current]);

  const isBarometerPage = BAROMETERS.some(b => b.href === current);
  const isPackagePage = PACKAGES.some(p => p.href === current);

  return (
    <header className="sticky top-0 z-50 bg-white" style={{ borderBottom: "1px solid #e5e7eb", boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-14">

          {/* Logo */}
          <a href="/" className="flex items-center gap-2.5 shrink-0">
            <img src={LOGO_URL} alt="WERBEAGENTUR Larissa Girenko" className="w-12 h-12 object-contain rounded-full" />
            {/* Logo-Text: LARISSA GIRENKO bestimmt die Breite (natürliche Breite bei ls=0.04em).
                WERBEAGENTUR bekommt mehr letter-spacing damit beide Zeilen gleich breit sind. */}
<LogoText />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1" aria-label="Hauptnavigation">

            {/* 1. Mein Paket™ – Primärer CTA */}
            <a
              href="/mein-paket"
              className="flex items-center gap-1.5 px-4 py-2 font-black uppercase tracking-widest text-xs text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: "#6d0000", fontFamily: "'Bebas Neue', sans-serif", fontSize: "0.8rem" }}
            >
              ✦ Mein Paket™
            </a>

            {/* 2. Barometer Dropdown */}
            <div ref={barometerRef} className="relative">
              <button
                onClick={() => { setBarometerOpen(!barometerOpen); setPaketeOpen(false); }}
                className="flex items-center gap-1 px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  color: isBarometerPage ? accentColor : "#374151",
                  borderBottom: isBarometerPage ? `2px solid ${accentColor}` : "2px solid transparent",
                }}
                aria-expanded={barometerOpen}
                aria-haspopup="true"
              >
                Barometer
                <svg className="w-3 h-3 ml-0.5 transition-transform" style={{ transform: barometerOpen ? "rotate(180deg)" : "rotate(0deg)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {barometerOpen && (
                <div className="absolute left-0 top-full mt-1 w-72 bg-white border border-gray-200 shadow-xl z-50" style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
                  <div className="p-2">
                    {BAROMETERS.map(b => (
                      <a
                        key={b.href}
                        href={b.href}
                        onClick={() => setBarometerOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors group"
                        style={{ borderLeft: b.href === current ? `3px solid ${b.color}` : "3px solid transparent" }}
                      >
                        <img src={b.icon} alt={b.label} className="w-5 h-5 flex-shrink-0 object-contain" style={{ opacity: 0.85 }} />
                        <div className="min-w-0">
                          <p className="text-xs font-bold uppercase tracking-widest leading-tight" style={{ color: b.href === current ? b.color : "#111", fontFamily: "'DM Sans', sans-serif" }}>
                            {b.label}
                          </p>
                          <p className="text-xs text-gray-400 leading-tight" style={{ fontFamily: "'DM Sans', sans-serif" }}>{b.desc}</p>
                        </div>
                        <span className="ml-auto text-gray-300 group-hover:text-gray-500 text-xs">→</span>
                      </a>
                    ))}
                  </div>
                  <div className="border-t border-gray-100 p-3">
                    <a href="#barometer-finder" onClick={() => setBarometerOpen(false)} className="flex items-center justify-center gap-2 w-full py-2 text-xs font-bold uppercase tracking-widest text-white transition-opacity hover:opacity-90" style={{ backgroundColor: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>
                      <img src="/manus-storage/icon_onpage_grundlagen_a1ddd99a.png" alt="" className="w-4 h-4 object-contain" style={{ filter: "brightness(0) invert(1)" }} />
                      Welches passt zu mir?
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* 3. Pakete Dropdown */}
            <div ref={paketeRef} className="relative">
              <button
                onClick={() => { setPaketeOpen(!paketeOpen); setBarometerOpen(false); }}
                className="flex items-center gap-1 px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  color: isPackagePage ? "#6d0000" : "#374151",
                  borderBottom: isPackagePage ? "2px solid #6d0000" : "2px solid transparent",
                }}
                aria-expanded={paketeOpen}
                aria-haspopup="true"
              >
                Pakete
                <svg className="w-3 h-3 ml-0.5 transition-transform" style={{ transform: paketeOpen ? "rotate(180deg)" : "rotate(0deg)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {paketeOpen && (
                <div className="absolute left-0 top-full mt-1 w-72 bg-white border border-gray-200 shadow-xl z-50" style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
                  <div className="p-2">
                    {PACKAGES.map(p => (
                      <a
                        key={p.href}
                        href={p.href}
                        onClick={() => setPaketeOpen(false)}
                        className="flex items-center justify-between gap-3 px-3 py-3 hover:bg-gray-50 transition-colors group"
                        style={{ borderLeft: p.href === current ? "3px solid #6d0000" : "3px solid transparent" }}
                      >
                        <div className="min-w-0">
                          <p className="text-xs font-bold uppercase tracking-widest leading-tight" style={{ color: p.href === current ? "#6d0000" : "#111", fontFamily: "'DM Sans', sans-serif" }}>
                            {p.label}
                          </p>
                          <p className="text-xs text-gray-400 leading-tight mt-0.5" style={{ fontFamily: "'DM Sans', sans-serif" }}>{p.desc}</p>
                        </div>
                        <span className="font-black text-sm flex-shrink-0" style={{ color: "#6d0000", fontFamily: "'Bebas Neue', sans-serif" }}>{p.price}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* 4. Kontakt-Button */}
            <a
              href="/kontakt"
              className="flex items-center gap-1.5 px-4 py-2 font-black uppercase tracking-widest text-xs transition-all border-2"
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: "0.8rem",
                borderColor: current === "/kontakt" ? "#115751" : "#115751",
                color: current === "/kontakt" ? "#fff" : "#115751",
                backgroundColor: current === "/kontakt" ? "#115751" : "transparent",
              }}
            >
              Kontakt
            </a>

            {/* Aktive Seite (wenn Barometer oder Paket) */}
            {(isBarometerPage || isPackagePage) && (
              <span className="flex items-center gap-1.5 px-3 py-1 text-xs font-bold uppercase tracking-widest border" style={{ borderColor: `${accentColor}40`, color: accentColor, fontFamily: "'DM Sans', sans-serif" }}>
                {BAROMETERS.find(b => b.href === current) && (
                  <img src={BAROMETERS.find(b => b.href === current)!.icon} alt="" className="w-4 h-4 object-contain" />
                )}
                {BAROMETERS.find(b => b.href === current)?.label || PACKAGES.find(p => p.href === current)?.label}
              </span>
            )}
          </nav>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden flex flex-col gap-1.5 p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menü öffnen"
            aria-expanded={mobileOpen}
          >
            <span className="block w-6 h-0.5 bg-gray-800 transition-all" style={{ transform: mobileOpen ? "rotate(45deg) translateY(8px)" : "none" }} />
            <span className="block w-6 h-0.5 bg-gray-800 transition-all" style={{ opacity: mobileOpen ? 0 : 1 }} />
            <span className="block w-6 h-0.5 bg-gray-800 transition-all" style={{ transform: mobileOpen ? "rotate(-45deg) translateY(-8px)" : "none" }} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 overflow-y-auto" style={{ maxHeight: "85vh" }}>
          {/* Mein Paket – Primär */}
          <div className="p-4 border-b border-gray-100">
            <div className="flex gap-2">
              <a
                href="/mein-paket"
                onClick={() => setMobileOpen(false)}
                className="flex-1 flex items-center justify-center gap-2 py-3.5 font-black uppercase tracking-widest text-sm text-white"
                style={{ backgroundColor: "#6d0000", fontFamily: "'Bebas Neue', sans-serif" }}
              >
                ✦ Mein Paket™
              </a>
              <a
                href="/kontakt"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center px-4 py-3.5 font-black uppercase tracking-widest text-sm border-2"
                style={{ borderColor: "#115751", color: "#115751", fontFamily: "'Bebas Neue', sans-serif" }}
              >
                Kontakt
              </a>
            </div>
          </div>

          {/* Barometer */}
          <div className="p-4 border-b border-gray-100">
            <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Einzelne Barometer
            </p>
            <div className="grid grid-cols-1 gap-1">
              {BAROMETERS.map(b => (
                <a
                  key={b.href}
                  href={b.href}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 transition-colors"
                  style={{
                    backgroundColor: b.href === current ? `${b.color}10` : "transparent",
                    borderLeft: b.href === current ? `3px solid ${b.color}` : "3px solid transparent",
                  }}
                >
                  <img src={b.icon} alt={b.label} className="w-6 h-6 flex-shrink-0 object-contain" style={{ opacity: 0.85 }} />
                  <div>
                    <p className="text-sm font-bold" style={{ color: b.href === current ? b.color : "#111", fontFamily: "'DM Sans', sans-serif" }}>{b.label}</p>
                    <p className="text-xs text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>{b.desc}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Pakete */}
          <div className="p-4 border-b border-gray-100">
            <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Kombi-Pakete
            </p>
            <div className="grid grid-cols-1 gap-1">
              {PACKAGES.map(p => (
                <a
                  key={p.href}
                  href={p.href}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-between px-3 py-2.5 transition-colors"
                  style={{
                    backgroundColor: p.href === current ? "#6d000010" : "transparent",
                    borderLeft: p.href === current ? "3px solid #6d0000" : "3px solid transparent",
                  }}
                >
                  <div>
                    <p className="text-sm font-bold" style={{ color: p.href === current ? "#6d0000" : "#111", fontFamily: "'DM Sans', sans-serif" }}>{p.label}</p>
                    <p className="text-xs text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>{p.desc}</p>
                  </div>
                  <span className="font-black text-sm" style={{ color: "#6d0000", fontFamily: "'Bebas Neue', sans-serif" }}>{p.price}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Finder */}
          <div className="p-4">
            <a
              href="#barometer-finder"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center gap-2 w-full py-3 border-2 font-bold text-sm uppercase tracking-widest transition-colors"
              style={{ borderColor: "#6d0000", color: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}
            >
              <img src="/manus-storage/icon_onpage_grundlagen_a1ddd99a.png" alt="" className="w-5 h-5 object-contain" />
              Welches Barometer passt zu mir?
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
